/**
 * Lightweight markdown → HTML for event descriptions. Ported from the Library's
 * public/js/markdown.js so Station previews match what the public event page
 * and announcement email render. Escapes first, then applies patterns — safe to
 * inject via dangerouslySetInnerHTML.
 */

function esc(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function inlineFormat(text: string): string {
  let s = esc(text);
  s = s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/__(.+?)__/g, "<strong>$1</strong>");
  s = s.replace(/\*(.+?)\*/g, "<em>$1</em>");
  s = s.replace(/_(.+?)_/g, "<em>$1</em>");
  // Links: only safe protocols; the URL is escaped for the attribute context.
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, label: string, url: string) => {
    const u = url.trim();
    if (/^(https?:\/\/|mailto:|\/)/i.test(u)) {
      const safeUrl = u.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/'/g, "&#39;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      return `<a href="${safeUrl}" style="color:var(--gold)" target="_blank" rel="noopener noreferrer">${label}</a>`;
    }
    return label;
  });
  return s;
}

export function mdToHtml(text: string | null | undefined): string {
  if (!text) return "";
  const lines = text.split("\n");
  const out: string[] = [];
  let inUl = false;
  let inOl = false;

  for (const line of lines) {
    if (inUl && !/^\s*[-*]\s/.test(line)) { out.push("</ul>"); inUl = false; }
    if (inOl && !/^\s*\d+\.\s/.test(line)) { out.push("</ol>"); inOl = false; }

    let m: RegExpMatchArray | null;
    if ((m = line.match(/^###\s+(.+)$/))) { out.push(`<h3>${inlineFormat(m[1])}</h3>`); continue; }
    if ((m = line.match(/^##\s+(.+)$/))) { out.push(`<h2>${inlineFormat(m[1])}</h2>`); continue; }
    if ((m = line.match(/^\s*[-*]\s+(.+)$/))) {
      if (!inUl) { out.push("<ul>"); inUl = true; }
      out.push(`<li>${inlineFormat(m[1])}</li>`);
      continue;
    }
    if ((m = line.match(/^\s*\d+\.\s+(.+)$/))) {
      if (!inOl) { out.push("<ol>"); inOl = true; }
      out.push(`<li>${inlineFormat(m[1])}</li>`);
      continue;
    }
    if (/^---+$/.test(line.trim())) { out.push("<hr>"); continue; }
    if (!line.trim()) continue;
    out.push(`<p>${inlineFormat(line)}</p>`);
  }
  if (inUl) out.push("</ul>");
  if (inOl) out.push("</ol>");
  return out.join("\n");
}

/** Result of a toolbar edit: the new value and where to put the caret/selection. */
export interface MdEdit { value: string; selectionStart: number; selectionEnd: number }

/** Wrap the selection with `prefix`…`suffix` (e.g. **bold**). */
export function wrapSelection(value: string, start: number, end: number, prefix: string, suffix = prefix): MdEdit {
  const selected = value.slice(start, end);
  if (selected) {
    return {
      value: value.slice(0, start) + prefix + selected + suffix + value.slice(end),
      selectionStart: start + prefix.length,
      selectionEnd: start + prefix.length + selected.length,
    };
  }
  return { value: value.slice(0, start) + prefix + suffix + value.slice(end), selectionStart: start + prefix.length, selectionEnd: start + prefix.length };
}

/** Insert a line prefix (heading, bullet, numbered) at the start of the current line. */
export function prefixLine(value: string, start: number, prefix: string): MdEdit {
  const lineStart = value.lastIndexOf("\n", start - 1) + 1;
  return {
    value: value.slice(0, lineStart) + prefix + value.slice(lineStart),
    selectionStart: start + prefix.length,
    selectionEnd: start + prefix.length,
  };
}

/** Turn the selection (or "link text") into a markdown link. */
export function insertLink(value: string, start: number, end: number, url: string): MdEdit {
  const selected = value.slice(start, end) || "link text";
  return {
    value: value.slice(0, start) + `[${selected}](${url})` + value.slice(end),
    selectionStart: start + 1,
    selectionEnd: start + 1 + selected.length,
  };
}
